'use client';

import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';

import Carousel from '@commons/components/Carousel';
import { useMediaQueries } from '@commons/contexts/responsive';
import { generateId } from '@commons/helpers/string';
import { getSpacingValue } from '@commons/helpers/styles';
import { useSize } from '@commons/hooks/useSize';
import PageBuilder from '@commons/pagebuilder';

import type { CarouselBlockProps } from '.';
import styles from './index.module.scss';

/**
 * CarouselBlock component.
 */

const CarouselBlock = ({ uuid, content }: CarouselBlockProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const [isMounted, setIsMounted] = useState(false);

  useLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  const {
    slides = [],
    autoplay = false,
    displayDots = false,
    displayArrows = true,
    displayCounter = false,
    bleedEffect = false,
  } = content;

  // Drupal returns a string, so we need to convert it to a number.
  const slidesToShow = Number(content.slidesToShow) || 1;

  // Get the gap or set it to `md` by default (e.g. if it's null or undefined).
  const gap = content.gap || 'md';

  const slidesPerView = useSlidesPerView({ slidesToShow, bleedEffect, size });

  const renderedSlides = slides.map((slide) => {
    const id = generateId(`slide-${uuid}-${slide.id}`);
    return <Carousel.Slide key={id}>{PageBuilder.renderParagraph(slide)}</Carousel.Slide>;
  });

  const hasControls = displayCounter || displayArrows || displayDots;
  const showControls = slidesToShow !== slides.length && hasControls;

  const offset = bleedEffect && size ? size.x : 0;
  const containerWidth = bleedEffect && size ? size.width : undefined;

  const classes = clsx(styles.wrapper, bleedEffect && styles.bleed, 'w-100');

  // The HTML returned by Swiper during SSR causes layout shifts, causing issues with other features like anchor links.
  // To prevent this, a loading component is rendered first to match the final layout.
  if (!isMounted) {
    const renderedLoadingSlides = Array.from({ length: slidesToShow }).map((_, index) =>
      PageBuilder.renderParagraph(slides[index])
    );

    return (
      <div ref={ref} className={classes}>
        <Carousel.Loading className={styles.loading}>
          {renderedLoadingSlides}
          {showControls && <Carousel.ControlsLoading />}
        </Carousel.Loading>
      </div>
    );
  }

  return (
    <div ref={ref} className={classes}>
      <Carousel
        spaceBetween={getSpacingValue(gap)}
        autoplay={autoplay}
        navigation={displayArrows}
        pagination={displayDots}
        slidesPerView={slidesPerView}
        slidesOffsetBefore={offset}
        slidesOffsetAfter={offset}
        className={styles.carousel}
      >
        {renderedSlides}
        {showControls && (
          <Carousel.Controls
            displayCounter={displayCounter}
            displayNavigation={displayArrows}
            displayPagination={displayDots}
            className={styles.controls}
            style={{ left: offset, width: containerWidth }}
          />
        )}
      </Carousel>
    </div>
  );
};

// Parameters for the useSlidesPerView hook.

type UseSlidesPerViewParams = {
  slidesToShow: number;
  bleedEffect: boolean;
  size?: DOMRectReadOnly;
};

/**
 * Custom hook to calculate the number of slides per view based on the screen size and the bleed effect.
 *
 * Since the width is overflowing with the bleed effect, a ratio is necessary to adjust the value of the slides per view.
 * This ratio is calculated with the `size` state, which is obtained after the `ref` is set.
 *
 * Unfortunatelly Swiper `breakpoints` property does not trigger a re-render, so we need to use `slidesPerView` and change it based on the screen size.
 */

const useSlidesPerView = ({ slidesToShow, bleedEffect, size }: UseSlidesPerViewParams) => {
  const { minWidth } = useMediaQueries();

  const isXl = minWidth?.xl;
  const isLg = minWidth?.lg;

  const slidesPerViewLg = slidesToShow >= 3 ? slidesToShow - 1 : slidesToShow;

  if (bleedEffect && size) {
    const clientWidth = document.documentElement.clientWidth;
    const slidesPerView = isXl ? slidesToShow : isLg ? slidesPerViewLg : 0.95;

    const ratio = Math.min(clientWidth, 1920) / size.width;

    return slidesPerView * ratio;
  }

  return isXl ? slidesToShow : isLg ? slidesPerViewLg : 1;
};

export default CarouselBlock;
